// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { isMaintenance } from '../utils/maintenanceMode';
import { useAuth } from './AuthContext';

const StargatesContext = createContext();

export const useStargates = () => {
  const context = useContext(StargatesContext);
  if (!context) {
    throw new Error('useStargates must be used within a StargatesProvider');
  }
  return context;
};

export const StargatesProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [stargates, setStargates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStargates = useCallback(async () => {
    if (!currentUser) {
      setStargates([]);
      return;
    }
    // Pause while a restore is swapping the databases (backend is down).
    if (isMaintenance()) return;

    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get('/stargates');
      setStargates(response.data);
    } catch (err) {
      if (err.response?.status !== 401 && err.response?.status !== 404) {
        console.error('Error fetching stargates:', err);
      }
      setError(err.message);
      setStargates([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const fetchStargateStatus = useCallback(async () => {
    if (!currentUser || stargates.length === 0) {
      return;
    }
    if (isMaintenance()) return;

    try {
      const response = await axiosInstance.get('/stargates/status');
      const statusMap = new Map(response.data.map((s) => [s.id, s]));
      setStargates((prev) =>
        prev.map((sg) => ({
          ...sg,
          ...statusMap.get(sg.id),
        }))
      );
    } catch (err) {
      if (err.response?.status !== 401 && err.response?.status !== 404) {
        console.error('Error fetching stargate status:', err);
      }
    }
  }, [currentUser, stargates.length]);

  useEffect(() => {
    if (currentUser) {
      fetchStargates();

      // Poll every 10 seconds so status transitions (and the alerts derived
      // from them) stay fresh.
      const interval = setInterval(fetchStargates, 10000);

      // Background tabs throttle setInterval — refetch as soon as the tab is
      // visible again so the list never looks stale after returning.
      const onVisible = () => {
        if (document.visibilityState === 'visible') fetchStargates();
      };
      document.addEventListener('visibilitychange', onVisible);

      return () => {
        clearInterval(interval);
        document.removeEventListener('visibilitychange', onVisible);
      };
    } else {
      // Clear stargates if user logs out
      setStargates([]);
    }
  }, [currentUser]);

  const value = useMemo(
    () => ({
      stargates,
      loading,
      error,
      refreshStargates: fetchStargates,
      refreshStatus: fetchStargateStatus,
    }),
    [stargates, loading, error, fetchStargates, fetchStargateStatus]
  );

  return <StargatesContext.Provider value={value}>{children}</StargatesContext.Provider>;
};
